import { OnDestroy, OnInit, } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Auth } from '@freelancer/auth';
import { Datastore, isDefined, toNumber, uniqWith, } from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingSize, HeadingType, HeadingWeight, } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontColor } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { debounceTime, map, startWith, take } from 'rxjs/operators';
var SkillsEditModalComponent = /** @class */ (function () {
    function SkillsEditModalComponent(auth, datastore, fb, modalRef) {
        this.auth = auth;
        this.datastore = datastore;
        this.fb = fb;
        this.modalRef = modalRef;
        this.BannerAlertType = BannerAlertType;
        this.ButtonColor = ButtonColor;
        this.FontColor = FontColor;
        this.HeadingSize = HeadingSize;
        this.HeadingType = HeadingType;
        this.HeadingWeight = HeadingWeight;
        this.Margin = Margin;
        this.OTHERS_CATEGORY = { name: 'Others', id: 99 };
        this._busySavingSkills$ = new Rx.Subject();
        this._changesMade$ = new Rx.BehaviorSubject(false);
        this._errorSaving$ = new Rx.Subject();
        this._selectedCategory$ = new Rx.Subject();
        this._selectedSkills$ = new Rx.BehaviorSubject([]);
        this._loadingSearchedForSkills$ = new Rx.Subject();
        this.busySavingSkills$ = this._busySavingSkills$.asObservable();
        this.errorSaving$ = this._errorSaving$.asObservable();
        this.selectedCategory$ = this._selectedCategory$.asObservable();
        this.selectedSkills$ = this._selectedSkills$.asObservable();
        this.loadingSearchedForSkills$ = this._loadingSearchedForSkills$.asObservable();
        this.skillFormGroup = new FormGroup({});
        this.skillSearchControl = new FormControl('');
    }
    SkillsEditModalComponent.prototype.ngOnInit = function () {
        var _this = this;
        var allSkillsCollection$ = this.datastore.collection('skills');
        this.userSkillsDocument = this.datastore.document('userSkills');
        var membershipBenefitsDocument$ = this.datastore.document('membershipBenefits', 'skills_limit');
        this.userId$ = this.auth.getUserId().pipe(map(function (id) { return toNumber(id); }));
        // Set a form group of all skills and pre-fill the selected skills.
        this.formSetupSubscription = Rx.combineLatest([
            this.userSkillsDocument.valueChanges(),
            allSkillsCollection$.valueChanges(),
        ]).subscribe(function (_a) {
            var usersSkills = _a[0], skills = _a[1];
            _this.skillFormGroup.reset();
            var selectedSkills = [];
            skills.forEach(function (skill) {
                var hasSkill = usersSkills.skills.includes(skill.id);
                if (hasSkill) {
                    selectedSkills.push(skill);
                }
                _this.skillFormGroup.addControl(skill.seoUrl, _this.fb.control(hasSkill));
            });
            _this._selectedSkills$.next(selectedSkills);
        });
        this.maxSkillsLimit$ = membershipBenefitsDocument$
            .valueChanges()
            .pipe(map(function (benefit) { return benefit.value; }));
        this.overMaxSkillsLimit$ = Rx.combineLatest([
            this.maxSkillsLimit$,
            this._selectedSkills$,
        ]).pipe(map(function (_a) {
            var maxSkills = _a[0], selectedSkills = _a[1];
            return selectedSkills.length > maxSkills;
        }));
        this.skillSearchControl.valueChanges.subscribe(function () {
            _this._loadingSearchedForSkills$.next(true);
        });
        this.showSaveButton$ = Rx.combineLatest([
            this.overMaxSkillsLimit$,
            this._changesMade$,
        ]).pipe(map(function (_a) {
            var overMaxSkillCount = _a[0], changesMade = _a[1];
            return !overMaxSkillCount && changesMade;
        }));
        this.matchingJobCount$ = this.selectedSkills$.pipe(map(function (skills) {
            return skills.reduce(function (prev, cur) { return prev + cur.activeProjectCount; }, 0);
        }));
        this.filteredSkills$ = Rx.combineLatest([
            allSkillsCollection$.valueChanges(),
            this.selectedCategory$.pipe(startWith(undefined)),
            this.skillSearchControl.valueChanges.pipe(startWith(undefined), debounceTime(500)),
        ]).pipe(map(function (_a) {
            var skills = _a[0], selectedCategory = _a[1], searchInputText = _a[2];
            // If the user has used the skill search input, prioritise over the
            // selected category. Search on both the skill name and category name.
            if (searchInputText) {
                var searchTerm_1 = searchInputText.toLowerCase();
                var filteredSkills = skills.filter(function (skill) {
                    return (skill.category &&
                        skill.category.name &&
                        skill.category.name.toLowerCase().includes(searchTerm_1)) ||
                        skill.name.toLowerCase().includes(searchTerm_1);
                });
                _this._loadingSearchedForSkills$.next(false);
                return filteredSkills;
            }
            // If a category has been selected return all skills with that category.
            if (selectedCategory) {
                var filteredSkills = skills.filter(function (skill) {
                    return skill.category && skill.category.id === selectedCategory.id;
                });
                _this._loadingSearchedForSkills$.next(false);
                return filteredSkills;
            }
            _this._loadingSearchedForSkills$.next(false);
            // The user has not selected a category or used the search box.
            return [];
        }));
        this.categories$ = allSkillsCollection$.valueChanges().pipe(
        // Take only unique categories from all active skills.
        map(function (skills) { return uniqWith(skills.map(function (skill) { return skill.category; }).filter(isDefined), function (t1, t2) { return t1.id === t2.id; })
            // Remove the 'Others' category and order by id.
            .filter(function (category) { return category.id !== _this.OTHERS_CATEGORY.id; })
            .sort(function (a, b) { return a.id - b.id; }).concat([
            // Appends the 'Others' category to the end of the list.
            _this.OTHERS_CATEGORY,
        ]); }));
    };
    SkillsEditModalComponent.prototype.handleSelectedCategory = function (selectedCategory) {
        this._selectedCategory$.next(selectedCategory);
        // Clear out the input text when selecting a category.
        this.skillSearchControl.reset(undefined);
    };
    SkillsEditModalComponent.prototype.handleSelectedSkill = function (selectedSkill) {
        var _this = this;
        this._changesMade$.next(true);
        this.selectedSkills$
            .pipe(take(1))
            .toPromise()
            .then(function (skills) {
            _this._selectedSkills$.next(skills.concat([selectedSkill]));
        });
    };
    SkillsEditModalComponent.prototype.handleDeselectedSkill = function (deselectedSkill) {
        var _this = this;
        this._changesMade$.next(true);
        this.selectedSkills$
            .pipe(take(1))
            .toPromise()
            .then(function (skills) {
            _this._selectedSkills$.next(skills.filter(function (skill) { return skill !== deselectedSkill; }));
        });
    };
    SkillsEditModalComponent.prototype.handleSaveSkills = function () {
        var _this = this;
        this._busySavingSkills$.next(true);
        this._errorSaving$.next(false);
        Rx.combineLatest([this.userId$, this.selectedSkills$])
            .pipe(take(1))
            .toPromise()
            .then(function (_a) {
            var userId = _a[0], selectedSkills = _a[1];
            var skillIds = selectedSkills.map(function (skill) { return skill.id; });
            var userSkill = {
                id: userId,
                skills: skillIds,
            };
            // Only update the skills if the user has at least 1 skill selected.
            if (skillIds.length > 0) {
                _this.userSkillsDocument
                    .update(userSkill)
                    .then(function (response) { return _this.handleSaveResponse(response); });
            }
            else {
                _this.userSkillsDocument
                    .remove()
                    .then(function (response) { return _this.handleSaveResponse(response); });
            }
        });
    };
    SkillsEditModalComponent.prototype.handleSaveResponse = function (response) {
        this._busySavingSkills$.next(false);
        if (response.status === 'error') {
            // TODO: implement a toast error message when live.
            this._errorSaving$.next(true);
            return;
        }
        // Close the modal with a boolean of if any changes were made. This is used
        // to determine if the user profile top skills needs to be updated.
        this.modalRef.close(this._changesMade$.getValue());
    };
    SkillsEditModalComponent.prototype.handleDeselectedSkillSelection = function (deselectedSkill) {
        var skill = this.skillFormGroup.get(deselectedSkill.seoUrl);
        if (skill) {
            skill.setValue(null);
        }
        this.handleDeselectedSkill(deselectedSkill);
    };
    SkillsEditModalComponent.prototype.ngOnDestroy = function () {
        if (this.formSetupSubscription) {
            this.formSetupSubscription.unsubscribe();
        }
    };
    return SkillsEditModalComponent;
}());
export { SkillsEditModalComponent };
